import React, { FC, useContext } from "react";
import Head from "next/head";
import { LogContext } from "context/log";
import { Heading } from "@pepdirect/ui/heading";
import { ExternalLink } from "@pepdirect/ui/ExternalLink";
import { META_TITLE, CANONICAL_URL_BASE } from "constants/meta";
import { links } from "config";
import st from "styles/modules/static-page.module.scss";
import { PAGE_TYPE_ENUM } from "@pepdirect/shared/types";
import { generatePageId } from "@pepdirect/helpers/analyticsLogger";

const faqs = [
  {
    question: `What is your Return policy?`,
    answer: (
      <>
        All sales are final, we do not offer any refunds or returns. Please
        contact us at{" "}
        <ExternalLink
          href={`mailto:${links.support}?subject=Question about SodaStream Professional order`}
        >
          {links.support}
        </ExternalLink>{" "}
        to let us know if you have any issues or concerns with your order.
      </>
    ),
  },
  {
    question: `What is your shipping policy?`,
    answer: (
      <ul>
        <li>Free standard shipping</li>
        <li>
          Your order will typically arrive within 2-4 business days. Please note
          the estimated delivery date on your order confirmation page or updated
          delivery dates in email notifications.
        </li>
        <li>
          Delivery times may vary, especially during peak periods.
          Unfortunately, we don’t currently ship to Alaska or Hawaii. We do not
          ship to Canada.
        </li>
      </ul>
    ),
  },
  {
    question: `When can I expect to receive my order?`,
    answer: (
      <ul>
        <li>The majority of orders arrive in 2-4 business days.</li>
        <li>Delays may occur due to new constraints from COVID-19.</li>
      </ul>
    ),
  },
  {
    question: `How do I know if my order has shipped?`,
    answer: `When your order ships, you will receive an email notification
      to let you know that your order has shipped with the tracking number.`,
  },
  {
    question: `How do I track my order?`,
    answer: `When your order ships, you will receive a shipment email.
      Click on the link in the email. You can use the map on the page to
      track your order or use the tracking number provided.`,
  },
  {
    question: `Can I cancel my order?`,
    answer: (
      <>
        Due to our speedy shipping policy, we do not support order cancellation.
        Please reach out to us at{" "}
        <ExternalLink
          href={`mailto:${links.support}?subject=Question about SodaStream Professional order`}
        >
          {links.support}
        </ExternalLink>{" "}
        with any concerns.
      </>
    ),
  },
  {
    question: `Where can I find dietary and nutritional information for
      these products?`,
    answer: `You can find the dietary and nutritional information for each
      bundle on the product detail page. Simply click on the left and right
      arrows in the image carousel to find the nutritional facts for each
      product.`,
  },
  {
    question: `Have comments or suggestions?`,
    answer: (
      <>
        Get in touch at{" "}
        <ExternalLink
          href={`mailto:${links.support}?subject=Comment / Suggestion for SodaStream Professional`}
        >
          {links.support}
        </ExternalLink>
        .
      </>
    ),
  },
];

const FaqPage: FC = () => {
  const { useLogPageView } = useContext(LogContext);

  const metaTitle = `FAQs and Return Policy – ${META_TITLE}`;
  const metaUrl = `${CANONICAL_URL_BASE}/faq`;
  const metaDescription =
    "Shop drinks online for your customers and enjoy free shipping directly to your front door";

  useLogPageView({
    pageType: PAGE_TYPE_ENUM.support,
    pageCategory: undefined,
    pageTitle: metaTitle,
    id: generatePageId("faq"),
  });
  return (
    <div className={st.wrapper}>
      <Head>
        <title>{metaTitle}</title>
        <meta name="og:title" content={metaTitle} key="og:title" />
        <link rel="canonical" href={metaUrl} />
        <meta name="og:url" content={metaUrl} key="og:url" />
        <meta name="description" content={metaDescription} key="description" />
        <meta
          name="og:description"
          content={metaDescription}
          key="og:description"
        />
      </Head>

      <Heading level="h1" size="xxl" center>
        FAQs
      </Heading>

      {faqs.map((faq, index) => (
        <div key={`faq-${index}`}>
          <Heading level="h2" size="m" className={st.heading}>
            {faq.question}
          </Heading>
          <div className={st.content}>{faq.answer}</div>
        </div>
      ))}
    </div>
  );
};

export default FaqPage;
